import FetchIdentityProvider from '@Application/Security/Sso/Queries/FetchIdentityProvider';
import fetchIdentityProvider from '@Application/Security/Sso/fetchIdentityProvider';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import SsoFormContainer from '@UI/Pages/Administration/Sso/SsoFormContainer';
import IdentityProviderStatusMessages from '@UI/Pages/Administration/Sso/IdentityProviderStatusMessages';
import ssoCreationSignal, {
	setSsoCreationOpen,
} from '@UI/Pages/Administration/Sso/Signals/ssoCreationSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import { Plus, ShieldQuestion } from 'lucide-react';
import * as React from 'react';
import { useStoreQuery, useDispatch } from '@verclam/synapse';
import { useSignalValue } from '@verclam/pulsar';
import { Button, Skeleton } from '@verclam/ui-kit';

const skeletonContainerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

/**
 * The first singleton administration screen of the project: it renders one form, not a card grid
 * (`guides/pages.md`, `specs/…/plan/specs.md` §8.3).
 */
const SsoContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const isCreationFormOpen = useSignalValue(ssoCreationSignal);

	// A signal is module-level state: it survives this component's unmount, unlike `useState`. Without
	// this reset, navigating away and back would reopen the creation form on its own.
	React.useEffect(() => () => setSsoCreationOpen(false), []);

	const {
		data: identityProviderResolution,
		error: identityProviderError,
		refetch: refetchIdentityProvider,
		isLoading,
	} = useStoreQuery({
		selector: (state) => state.ssoStore.identityProviderResolution,
		stopOn: (resolution) => resolution.type !== 'unresolved' && resolution.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchIdentityProvider(new FetchIdentityProvider(accountId)));
		},
		deps: [accountId],
	});

	const identityProvider =
		identityProviderResolution?.type === 'configured'
			? identityProviderResolution.identityProvider
			: null;

	const handleStartCreation = React.useCallback(() => {
		setSsoCreationOpen(true);
	}, []);

	if (accountId === null) {
		return (
			<EmptyState
				title="Aucun compte"
				description="Vous n’êtes rattaché à aucun compte : le fournisseur d’identité ne peut pas être configuré."
				icon={<ShieldQuestion size={28} />}
			/>
		);
	}

	if (isLoading) {
		return (
			<div css={skeletonContainerStyle}>
				<Skeleton
					height="8rem"
					borderRadius={theme.radii.xlarge}
					animation="wave"
					shape="rectangle"
				/>
				<Skeleton
					height="16rem"
					borderRadius={theme.radii.xlarge}
					animation="wave"
					shape="rectangle"
				/>
			</div>
		);
	}

	if (identityProviderResolution === null && identityProviderError !== null) {
		return (
			<ErrorState
				title="Impossible de charger la configuration du fournisseur d’identité"
				description="Une erreur est survenue pendant le chargement de la configuration."
				onRetry={refetchIdentityProvider}
			/>
		);
	}

	if (identityProviderResolution?.type === 'absent' && !isCreationFormOpen) {
		return (
			<EmptyState
				title="Aucun fournisseur d’identité configuré"
				description="Connectez Dixit à votre fournisseur d’identité pour ouvrir la connexion unique (SSO)."
				icon={<ShieldQuestion size={28} />}
				action={
					<Button
						type="button"
						label="Configurer le fournisseur d’identité"
						icon={<Plus size={16} />}
						onClick={handleStartCreation}
					/>
				}
			/>
		);
	}

	return (
		<React.Fragment>
			<IdentityProviderStatusMessages identityProvider={identityProvider} />

			<SsoFormContainer identityProvider={identityProvider} accountId={accountId} />
		</React.Fragment>
	);
};

export default SsoContent;
